<script setup>
import { computed, ref } from 'vue';
import { longDate } from '../../helpers/usDate';
import { EditorContent, useEditor } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import CharacterCounter from './CharacterCounter.vue';
import CommsEditorPage from './CommsEditorPage.vue';
import MergeFieldButtons from './MergeFieldButtons.vue';
import SafeRichText from './SafeRichText.vue';
import XlCard from '../xl/XlCard.vue';
import XlDropdown from '../xl/XlDropdown.vue';
import XlField from '../xl/XlField.vue';
import { SAMPLE, withSamples } from './mergeFieldSamples';

const NAME_MAX = 60;
const SUBJECT_MAX = 100;
const PREHEADER_MAX = 150;
const BODY_MAX = 2000;
// Email is the only channel: chat went with Hub Announcements (XN-4389). A
// template saved with chat before then is saved back as email.
const CHANNELS = ['email'];

const props = defineProps({
  // null = a new template.
  template: {
    type: Object,
    default: null,
  },
  // Offered as a starting point for a NEW template only. Editing an existing
  // one and silently replacing what somebody wrote would be a data loss the
  // dropdown gives no warning about.
  standardTemplates: {
    type: Array,
    default: () => [],
  },
  // CommunicationsController::settings — the address the email really goes
  // out from, which is the platform's, not the hub contact's.
  sender: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['save', 'cancel']);

const isStandard = computed(() => props.template?.is_standard === true);

const BLANK = '';
const startFrom = ref(BLANK);

const startFromOptions = computed(() => [
  { value: BLANK, label: 'Blank template' },
  ...props.standardTemplates.map((template) => ({ value: String(template.id), label: template.name })),
]);

const applyStartingPoint = (value) => {
  startFrom.value = value;

  const source = props.standardTemplates.find((template) => String(template.id) === value);

  if (!source) {
    subject.value = '';
    preheader.value = '';
    bodyHtml.value = '';
    editor.value?.commands.setContent('');
    return;
  }

  // The name is left alone deliberately: copying it produces two rows called
  // "Renewal Reminder", and naming the thing is the one decision the person
  // starting from a standard template still has to make.
  subject.value = source.subject ?? '';
  preheader.value = source.preheader ?? '';
  bodyHtml.value = source.body_html ?? '';
  editor.value?.commands.setContent(source.body_html ?? '');
};

const name = ref(props.template?.name ?? '');
const subject = ref(props.template?.subject ?? '');
const preheader = ref(props.template?.preheader ?? '');
const bodyHtml = ref(props.template?.body_html ?? '');

const editor = useEditor({
  content: bodyHtml.value,
  extensions: [
    StarterKit.configure({
      heading: false,
      blockquote: false,
      code: false,
      codeBlock: false,
      horizontalRule: false,
      strike: false,
      link: false,
    }),
  ],
  editorProps: {
    attributes: { role: 'textbox', 'aria-multiline': 'true', 'aria-labelledby': 'template-message-label' },
  },
  onUpdate: ({ editor: e }) => {
    bodyHtml.value = e.getHTML();
  },
});

// jsdom has no real contenteditable, same limitation BannerModal.vue's own
// setBodyForTest hook works around.
const setBodyForTest = (html) => {
  bodyHtml.value = html;
  editor.value?.commands.setContent(html);
};

// Read from CharacterCounter's own exposed state, never re-derived here —
// see the comment on CharacterCounter.vue's defineExpose.
const nameCounter = ref(null);
const bodyCounter = ref(null);

const nameOverLimit = computed(() => nameCounter.value?.isOver ?? false);
const bodyOverLimit = computed(() => bodyCounter.value?.isOver ?? false);
const linesTooLong = computed(() => subject.value.length > SUBJECT_MAX || preheader.value.length > PREHEADER_MAX);

const nameBlank = computed(() => name.value.trim().length === 0);
const bodyBlank = computed(() => (bodyCounter.value?.visibleLength ?? 0) === 0);

const canSave = computed(
  () =>
    !nameBlank.value &&
    !nameOverLimit.value &&
    !linesTooLong.value &&
    !bodyOverLimit.value &&
    !bodyBlank.value
);

const save = () => {
  if (!canSave.value) return;
  emit('save', {
    name: name.value,
    channels: CHANNELS,
    subject: subject.value || null,
    preheader: preheader.value || null,
    body_html: bodyHtml.value,
  });
};

// Matches Navigation.vue's own fallback chain for the hub's display name.
const fromLine = computed(() => {
  const fromName = props.sender?.from_name || window.store?.subdomainName || window.store?.subdomain || '';
  return props.sender?.from_email ? `${fromName} <${props.sender.from_email}>` : fromName;
});

const emailPreviewHtml = computed(() => withSamples(bodyHtml.value));
const emailPreviewSubject = computed(() => withSamples(subject.value));
const emailPreviewPreheader = computed(() => withSamples(preheader.value));

const previewCaption =
  `Fields shown with sample data: ${SAMPLE.first_name} · ${SAMPLE.policy_line} · ${SAMPLE.renewal_date} · ${SAMPLE.broker}.`;

const lastEditedLabel = computed(() => {
  const edited = longDate(props.template?.updated_at);
  return edited ? `Last edited ${edited}` : '';
});

const FORMATS = [
  { mark: 'bold', label: 'Bold', text: 'B', run: (chain) => chain.toggleBold() },
  { mark: 'italic', label: 'Italic', text: 'I', run: (chain) => chain.toggleItalic() },
  { mark: 'underline', label: 'Underline', text: 'U', run: (chain) => chain.toggleUnderline() },
  { mark: 'bulletList', label: 'Bulleted list', text: '•', run: (chain) => chain.toggleBulletList() },
  { mark: 'orderedList', label: 'Numbered list', text: '1.', run: (chain) => chain.toggleOrderedList() },
];

const format = (item) => item.run(editor.value.chain().focus()).run();

defineExpose({ editor, canSave, save, setBodyForTest });
</script>

<template>
  <CommsEditorPage
    testid="template-editor"
    :changes="{ name, subject, preheader, body: bodyHtml, startFrom }"
    back-label="Back to templates"
    :title="props.template ? 'Edit template' : 'New template'"
    save-label="Save template"
    :save-disabled="!canSave"
    @back="emit('cancel')"
    @cancel="emit('cancel')"
    @save="save"
  >
    <template v-if="props.template" #meta>
      <p v-if="isStandard" data-testid="standard-edit-note">
        Changes apply to this hub's standard template — sequences using it will use the updated version next time they send.
      </p>
      <p v-else>Changes save to this template only — sequences using it will use the updated version next time they send.</p>
      <p v-if="lastEditedLabel" data-testid="template-last-edited">{{ lastEditedLabel }}</p>
    </template>

    <div class="template-editor__form">
      <div v-if="!props.template">
        <p id="template-start-from-label" class="template-editor__label">
          Start from a standard template <span class="template-editor__label-hint">— optional</span>
        </p>
        <XlDropdown
          class="template-editor__start-from"
          data-testid="template-start-from"
          aria-label="Start from a standard template"
          :options="startFromOptions"
          :model-value="startFrom"
          @update:model-value="applyStartingPoint"
        />
      </div>

      <XlField
        v-model="name"
        name="template_name"
        data-testid="template-name-input"
        label="Template name"
        placeholder="e.g. Storm alert — Chicago"
        :maxlength="NAME_MAX"
      >
        <template #counter>
          <CharacterCounter ref="nameCounter" data-testid="name-counter" :value="name" :max="NAME_MAX" plain-text />
        </template>
      </XlField>

      <div>
        <label class="template-editor__label" for="template_subject">
          Subject <span class="template-editor__label-hint">— keep under 60 characters so it isn't cut off on mobile</span>
        </label>
        <XlField
          v-model="subject"
          name="template_subject"
          data-testid="template-subject-input"
          :maxlength="SUBJECT_MAX"
          :counted="false"
        />
      </div>

      <div>
        <label class="template-editor__label" for="template_preheader">
          Preview line <span class="template-editor__label-hint">— shown after the subject in most inboxes</span>
        </label>
        <XlField
          v-model="preheader"
          name="template_preheader"
          data-testid="template-preheader-input"
          :maxlength="PREHEADER_MAX"
          :counted="false"
        />
      </div>

      <div>
        <p id="template-message-label" class="template-editor__label">Message</p>
        <div v-if="editor" class="template-editor__toolbar" role="toolbar" aria-label="Text formatting">
          <button
            v-for="item in FORMATS"
            :key="item.mark"
            type="button"
            :class="[`template-editor__format--${item.mark}`, { active: editor.isActive(item.mark) }]"
            :aria-label="item.label"
            :aria-pressed="editor.isActive(item.mark) ? 'true' : 'false'"
            @click="format(item)"
          >{{ item.text }}</button>
        </div>
        <editor-content :editor="editor" class="template-editor__editor" data-testid="template-body-editor" />
        <div class="template-editor__foot">
          <CharacterCounter ref="bodyCounter" data-testid="body-counter" :value="bodyHtml" :max="BODY_MAX" />
        </div>
      </div>

      <MergeFieldButtons :editor="editor" />
    </div>

    <template #aside>
      <XlCard padded class="template-editor__preview">
        <b class="template-editor__preview-title">Email preview</b>
        <XlCard class="template-editor__preview-card">
          <div class="template-editor__preview-head">
            <div><b>From:</b> {{ fromLine }}</div>
            <div><b>To:</b> jordan.p@example.com</div>
          </div>
          <div class="template-editor__preview-main">
            <b class="template-editor__preview-subject" data-testid="preview-email-subject">{{ emailPreviewSubject || 'Subject line' }}</b>
            <p v-if="emailPreviewPreheader" class="template-editor__preview-preheader" data-testid="preview-email-preheader">{{ emailPreviewPreheader }}</p>
            <div v-if="bodyBlank" class="template-editor__preview-body">Body</div>
            <SafeRichText v-else class="template-editor__preview-body" data-testid="preview-email-body" :html="emailPreviewHtml" />
          </div>
        </XlCard>
        <p class="template-editor__preview-caption" data-testid="preview-caption">{{ previewCaption }}</p>
      </XlCard>
    </template>
  </CommsEditorPage>
</template>

<style scoped lang="scss">
.template-editor {
  &__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    background-color: inherit;
  }

  &__label {
    display: block;
    margin: 0 0 6px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--xl-color-secondary-text);
  }

  &__start-from { display: flex; }

  &__label-hint {
    font-weight: 400;
    color: var(--xl-color-outline-variant);
  }

  &__toolbar {
    display: flex;
    gap: 5px;
    margin-bottom: 7px;

    button {
      width: 32px;
      height: 32px;
      flex: 0 0 32px;
      border: 1px solid var(--xl-color-border);
      border-radius: 11px;
      background: transparent;
      color: var(--xl-color-primary-text);
      font-family: inherit;
      font-size: 13px;
      cursor: pointer;

      &:hover { background: var(--xl-color-background); }
      &.active { background: var(--xl-color-primary-container); color: var(--xl-color-primary); }
    }
  }

  &__format--bold { font-weight: 700; }
  &__format--italic { font-style: italic; }
  &__format--underline { text-decoration: underline; }

  // The board's eight-row textarea: 8 × 21.75px of text inside an 11px/14px
  // inset, scrolling past that and resizable like one.
  &__editor {
    :deep(.tiptap) {
      box-sizing: border-box;
      height: 198px;
      min-height: 198px;
      overflow-y: auto;
      resize: vertical;
      padding: 11px 14px;
      border: 1px solid var(--xl-color-border);
      border-radius: 11px;
      background: #fff;
      outline: none;
      font-size: var(--xl-font-size-base);
      line-height: 1.5;
      color: var(--xl-color-primary-text);

      &:focus {
        border-color: var(--xl-color-primary);
        box-shadow: 0 0 0 3px rgba(94, 47, 120, 0.1);
      }

      p { margin: 0 0 1.5em; }
      > :last-child { margin-bottom: 0; }
    }
  }

  &__foot {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-top: 5px;
    font-size: 11.5px;
    color: var(--xl-color-outline-variant);

    .character-counter { flex: none; margin-left: auto; white-space: nowrap; }
  }

  &__preview-title { font-size: 15px; }

  &__preview-card {
    margin: 14px 0 0;
    overflow: hidden;
    background: var(--xl-color-background);
  }

  &__preview-head {
    padding: 14px 16px;
    border-bottom: 1px solid var(--xl-color-border);
    font-size: 12.5px;
    color: var(--xl-color-outline);

    b { color: var(--xl-color-primary-text); }
    > div + div { margin-top: 3px; }
  }

  &__preview-main { padding: 16px; }

  &__preview-subject {
    display: block;
    margin-bottom: 10px;
    font-size: 15px;
  }

  &__preview-preheader {
    margin: -4px 0 10px;
    font-size: 12.5px;
    color: var(--xl-color-outline);
  }

  &__preview-body {
    font-size: 14px;
    line-height: 1.6;
    color: var(--xl-color-secondary-text);

    :deep(p), :deep(ul), :deep(ol) { margin: 0 0 1.6em; }
    :deep(*:last-child) { margin-bottom: 0; }
    :deep(a) { color: var(--xl-color-primary); font-weight: 600; }
  }

  &__preview-caption {
    margin: 12px 0 0;
    font-size: 12.5px;
    color: var(--xl-color-outline);
  }
}
</style>
